import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

import { subscribePaymentRequired } from '../services/api.js';

/**
 * ToastContext — top-right stack of typed toasts that auto-dismiss after 3s.
 *
 * Usage:
 *   const { showToast } = useToast();
 *   showToast('Agent nudged');                        // info
 *   showToast('Showing confirmed', { type: 'success' });
 *   showToast('Update failed', { type: 'error' });
 */

const ToastContext = createContext(null);

const DEFAULT_DURATION = 3000;

const TYPE_STYLES = {
  success: {
    bar: 'bg-[#22C55E]',
    ring: 'ring-[#22C55E]/30',
    icon: CheckCircle2,
    iconClass: 'text-[#16A34A]',
  },
  error: {
    bar: 'bg-[#EF4444]',
    ring: 'ring-[#EF4444]/30',
    icon: AlertCircle,
    iconClass: 'text-[#DC2626]',
  },
  info: {
    bar: 'bg-[#3B82F6]',
    ring: 'ring-[#3B82F6]/30',
    icon: Info,
    iconClass: 'text-[#2563EB]',
  },
  warning: {
    bar: 'bg-[#F59E0B]',
    ring: 'ring-[#F59E0B]/30',
    icon: AlertTriangle,
    iconClass: 'text-[#D97706]',
  },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, options = {}) => {
      // Back-compat: first arg can be an object with { message, type, ... }.
      const opts =
        typeof message === 'object' && message !== null
          ? { ...message, ...options }
          : { message, ...options };
      const text = opts.message ?? '';
      const type = TYPE_STYLES[opts.type] ? opts.type : 'info';
      const durationMs = opts.durationMs ?? DEFAULT_DURATION;
      const action = opts.action;

      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message: text, type, action }]);
      if (durationMs > 0) {
        window.setTimeout(() => dismiss(id), durationMs);
      }
      return id;
    },
    [dismiss],
  );

  // Global 402 — surface an enrichment credits toast with a billing link.
  useEffect(() => {
    return subscribePaymentRequired(() => {
      showToast('Enrichment credits exhausted — buy more to keep enriching.', {
        type: 'warning',
        durationMs: 6000,
        action: { label: 'Buy more', href: '/settings#billing' },
      });
    });
  }, [showToast]);

  const value = useMemo(() => ({ showToast, dismissToast: dismiss }), [showToast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed top-6 right-6 z-[100] flex flex-col items-end gap-2
                   pointer-events-none print:hidden"
        aria-live="polite"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((t) => (
          <ToastPill
            key={t.id}
            message={t.message}
            type={t.type}
            action={t.action}
            onDismiss={() => dismiss(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastPill({ message, type, action, onDismiss }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(r);
  }, []);

  const styles = TYPE_STYLES[type] || TYPE_STYLES.info;
  const Icon = styles.icon;

  return (
    <div
      role="status"
      className={`pointer-events-auto w-[320px] max-w-[92vw] overflow-hidden
                  rounded-[8px] bg-white shadow-lg ring-1 ${styles.ring}
                  flex items-stretch
                  transition-all duration-200 ease-out ${
                    entered ? 'translate-x-0 opacity-100' : 'translate-x-3 opacity-0'
                  }`}
    >
      <span className={`w-1.5 shrink-0 ${styles.bar}`} aria-hidden="true" />
      <div className="flex items-start gap-3 px-3 py-2.5 flex-1 min-w-0">
        <Icon size={18} className={`shrink-0 mt-0.5 ${styles.iconClass}`} />
        <div className="flex-1 min-w-0 text-sm text-[#1F2937]">
          <p className="leading-snug break-words">{message}</p>
          {action?.href && (
            <a
              href={action.href}
              className="mt-1 inline-block text-xs font-semibold text-accent hover:underline"
            >
              {action.label}
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="shrink-0 text-[#9CA3AF] hover:text-[#4B5563] transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
